import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

export default function CustomerValue() {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get('/api/meters/customer-value', { headers })
      .then(res => {
        setMeters(res.data.meters);
        setLoading(false);
      })
      .catch(err => {
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate('/login');
        }
        setError('Failed to load customer value data');
        setLoading(false);
      });
  }, []);

  // Get medal for top 3
  const getMedal = (index) => {
    if (index === 0) return '1st';
    if (index === 1) return '2nd';
    if (index === 2) return '3rd';
    return index + 1;
  };

  // Get bar width percentage relative to highest value
  const maxValue = meters.length > 0 
    ? Math.max(...meters.map(m => parseFloat(m.total_transacted))) 
    : 1;

  return (
    <div style={{ minHeight: "100vh" }}>
      <Navbar user={user} />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes growBar { from { width: 0; } to { width: var(--bar-width); } }
      `}</style>

      <div className="mw-page" style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}>

        {/* Header */}
        <div data-tour="customer-value" style={{ marginBottom: 32, animation: "fadeIn 0.5s ease" }}>
          <h1 style={{ color: "#0f172a", fontSize: 26, fontWeight: 700, margin: 0 }}>
            Customer Value
          </h1>
          <p style={{ color: "#475569", fontSize: 14, marginTop: 6 }}>
            Meters ranked by total amount transacted — highest first
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "14px 18px", color: "#f87171", marginBottom: 24 }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(56,189,248,0.15)", borderTop: "3px solid #38bdf8", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "#475569" }}>Loading customer value...</p>
          </div>
        )}

        {!loading && (
          <>
            {meters.length === 0 ? (
              <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", padding: "60px 24px", textAlign: "center" }}>
                <p style={{ color: "#cbd5e1", fontSize: 15 }}>No transaction data yet</p>
                <p style={{ color: "#cbd5e1", fontSize: 14.5, marginTop: 8 }}>
                  Meters will appear here once transactions are recorded
                </p>
              </div>
            ) : (
              <>
                {/* Top 3 cards */}
                {meters.length >= 3 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 16, marginBottom: 32, animation: "fadeIn 0.5s ease" }}>
                    {meters.slice(0, 3).map((m, i) => (
                      <div key={m.id} style={{
                        background: "rgba(15,23,42,0.92)",
                        borderRadius: 16,
                        border: `1px solid ${i === 0 ? "rgba(251,191,36,0.3)" : i === 1 ? "rgba(148,163,184,0.3)" : "rgba(180,120,60,0.3)"}`,
                        padding: 24,
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: 32, marginBottom: 12 }}>{getMedal(i)}</div>
                        <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 15, marginBottom: 4, fontFamily: "monospace" }}>
                          {m.account_number}
                        </div>
                        <div style={{ color: "#cbd5e1", fontSize: 13.5, marginBottom: 12 }}>
                          {m.description || m.account_name || '—'}
                        </div>
                        <div style={{ color: i === 0 ? "#fbbf24" : i === 1 ? "#e2e8f0" : "#cd7f32", fontWeight: 800, fontSize: 20 }}>
                          KES {Number(m.total_transacted).toLocaleString()}
                        </div>
                        <div style={{ color: "#cbd5e1", fontSize: 13.5, marginTop: 6 }}>
                          {m.transaction_count} transactions
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Full ranked table with bar chart */}
                <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden", animation: "fadeIn 0.6s ease" }}>
                  <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                    <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>
                      All Meters — Ranked by Value
                    </h3>
                  </div>

                  <div style={{ padding: "8px 0" }}>
                    {meters.map((m, i) => (
                      <div key={m.id} style={{
                        padding: "16px 24px",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        animation: `fadeIn ${0.3 + i * 0.05}s ease`
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                            {/* Rank */}
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(56,189,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: i < 3 ? 16 : 13, fontWeight: 700, color: i < 3 ? '#fbbf24' : "#cbd5e1", flexShrink: 0 }}>
                              {getMedal(i)}
                            </div>
                            {/* Meter info */}
                            <div>
                              <div style={{ color: "#38bdf8", fontWeight: 700, fontSize: 15, fontFamily: "monospace" }}>
                                {m.account_number}
                              </div>
                              <div style={{ color: "#cbd5e1", fontSize: 13.5, marginTop: 2 }}>
                                {m.description && `${m.description} · `}
                                {m.account_name && `${m.account_name} · `}
                                {user.role === 'admin' && m.owner_name && `${m.owner_name} · `}
                                {m.transaction_count} transactions
                                {m.last_transaction && ` · Last: ${new Date(m.last_transaction).toLocaleDateString()}`}
                              </div>
                            </div>
                          </div>

                          {/* Value and status */}
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div style={{ color: "#10b981", fontWeight: 800, fontSize: 16 }}>
                              KES {Number(m.total_transacted).toLocaleString()}
                            </div>
                            <span style={{
                              background: m.status === 'active' ? "rgba(16,185,129,0.1)" : "rgba(248,113,113,0.1)",
                              border: `1px solid ${m.status === 'active' ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}`,
                              borderRadius: 6, padding: "2px 8px",
                              color: m.status === 'active' ? "#10b981" : "#f87171",
                              fontSize: 12, fontWeight: 600
                            }}>
                              {m.status}
                            </span>
                          </div>
                        </div>

                        {/* Value bar */}
                        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                          <div style={{
                            height: "100%",
                            width: `${(parseFloat(m.total_transacted) / maxValue) * 100}%`,
                            background: i === 0 ? "linear-gradient(90deg, #fbbf24, #f59e0b)" : i === 1 ? "linear-gradient(90deg, #e2e8f0, #cbd5e1)" : i === 2 ? "linear-gradient(90deg, #cd7f32, #b45309)" : "linear-gradient(90deg, #0891b2, #38bdf8)",
                            borderRadius: 4,
                            transition: "width 1s ease"
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}